import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../../services/supabaseClient';
import Header from '../../../components/Header';
import { Helmet } from 'react-helmet'; 
import { 
  MapPin, Calendar, BedDouble, Bath, ArrowLeft, Shield, Home, 
  CheckCircle, X, Phone, DollarSign, MessageSquare, Building, 
  View, Map, GraduationCap, ShoppingCart, Bus, TreePine,
  Search, Dumbbell, Wrench, Pill, AlertTriangle, Trash2 
} from 'lucide-react';

import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const createCustomIcon = (color: string, emoji: string) => {
  return L.divIcon({
    className: 'custom-leaflet-icon',
    html: `<div style="background-color: ${color}; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center; border-radius: 50%; color: white; font-size: 16px; border: 2px solid white; box-shadow: 0 4px 6px rgba(0,0,0,0.1);">${emoji}</div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
  });
};

const calculateDistanceKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
  const R = 6371; 
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLon = (lon2 - lon1) * (Math.PI / 180);
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return (R * c).toFixed(2);
};

const getOptimizedImageUrl = (url: string, width = 800) => {
  if (url && url.includes('supabase.co')) {
    return `${url}?width=${width}&quality=80&format=webp`;
  }
  return url;
};

export default function HabitatListing() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [listing, setListing] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  
  const [landlordName, setLandlordName] = useState('Propriétaire vérifié');
  const [showApplyModal, setShowApplyModal] = useState(false);
  const [show3DModal, setShow3DModal] = useState(false);
  const [coordinates, setCoordinates] = useState<[number, number] | null>(null);
  
  const [activeCategory, setActiveCategory] = useState<'education' | 'groceries' | 'transit' | 'parks' | 'pharmacies' | 'gyms' | 'garages'>('groceries');
  const [searchPoi, setSearchPoi] = useState('');
  const [pois, setPois] = useState<any>({});
  const [loadingPois, setLoadingPois] = useState(false);
  const [poiError, setPoiError] = useState(false);
  
  const [applyForm, setApplyForm] = useState({
    phone: '', income: '', message: 'Bonjour, je suis très intéressé(e) par ce logement et j\'aimerais planifier une visite.'
  });

  useEffect(() => {
    if (id) fetchListing(id);
  }, [id]);

  const fetchListing = async (listingId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUser(user);

      const { data, error } = await supabase.from('habitat_listings').select('*').eq('id', listingId).single();
      
      if (data) {
        setListing(data);
        if (user) {
          const { data: app } = await supabase.from('habitat_applications').select('id').eq('tenant_id', user.id).eq('listing_id', data.id).maybeSingle();
          if (app) setHasApplied(true);
        }
        
        const { data: profile } = await supabase.from('habitat_profiles').select('full_name').eq('id', data.landlord_id).maybeSingle();
        if (profile?.full_name) setLandlordName(profile.full_name);

        if (data.lat && data.lng) {
          setCoordinates([data.lat, data.lng]);
          fetchRealPOIs(data.lat, data.lng);
        } else {
          geocodeAddress(data.address, data.city);
        }
      }
    } catch (err) {
      console.error("Erreur de chargement", err);
    } finally {
      setLoading(false);
    }
  };

  const geocodeAddress = async (address: string, city: string) => {
    try {
      const query = encodeURIComponent(`${address}, ${city}, Quebec, Canada`);
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}`);
      const geodata = await res.json();
      let lat = 46.8139; let lon = -71.2080;
      if (geodata && geodata.length > 0) {
        lat = parseFloat(geodata[0].lat); lon = parseFloat(geodata[0].lon);
      }
      setCoordinates([lat, lon]);
      fetchRealPOIs(lat, lon);
    } catch (e) {
      setCoordinates([46.8139, -71.2080]);
    }
  };

  const fetchRealPOIs = async (lat: number, lon: number) => {
    setLoadingPois(true);
    setPoiError(false);
    const radius = 1500; 
    const query = `[out:json][timeout:25];(nwr["amenity"~"school|college|university|kindergarten"](around:${radius},${lat},${lon});nwr["shop"~"supermarket|convenience|grocery"](around:${radius},${lat},${lon});nwr["amenity"="pharmacy"](around:${radius},${lat},${lon});nwr["leisure"="fitness_centre"](around:${radius},${lat},${lon});nwr["shop"="car_repair"](around:${radius},${lat},${lon});nwr["highway"="bus_stop"](around:${radius},${lat},${lon});nwr["leisure"="park"](around:${radius},${lat},${lon}););out center;`;

    try {
      const response = await fetch('https://overpass-api.de/api/interpreter', { method: 'POST', body: query });
      if (!response.ok) throw new Error("429");
      
      const data = await response.json();
      const realPois: any = { education: [], groceries: [], pharmacies: [], gyms: [], garages: [], transit: [], parks: [] };

      if (data.elements) {
        data.elements.forEach((el: any) => {
          const tags = el.tags || {};
          const name = tags.name || tags.brand;
          if (!name && tags.highway !== 'bus_stop') return;
          const poiLat = el.lat || el.center?.lat;
          const poiLon = el.lon || el.center?.lon;
          if (!poiLat || !poiLon) return;

          const finalName = name || 'Arrêt de transport';
          if (tags.amenity && /school|college|university|kindergarten/.test(tags.amenity)) realPois.education.push({ name: finalName, lat: poiLat, lon: poiLon, type: 'education', icon: '🏫', color: '#3b82f6' });
          else if (tags.shop && /supermarket|convenience|grocery/.test(tags.shop)) realPois.groceries.push({ name: finalName, lat: poiLat, lon: poiLon, type: 'groceries', icon: '🛒', color: '#f59e0b' });
          else if (tags.amenity === 'pharmacy') realPois.pharmacies.push({ name: finalName, lat: poiLat, lon: poiLon, type: 'pharmacies', icon: '💊', color: '#ef4444' });
          else if (tags.leisure === 'fitness_centre') realPois.gyms.push({ name: finalName, lat: poiLat, lon: poiLon, type: 'gyms', icon: '🏋️', color: '#f97316' });
          else if (tags.shop === 'car_repair') realPois.garages.push({ name: finalName, lat: poiLat, lon: poiLon, type: 'garages', icon: '🚗', color: '#64748b' });
          else if (tags.highway === 'bus_stop') realPois.transit.push({ name: finalName, lat: poiLat, lon: poiLon, type: 'transit', icon: '🚌', color: '#8b5cf6' });
          else if (tags.leisure === 'park') realPois.parks.push({ name: finalName, lat: poiLat, lon: poiLon, type: 'parks', icon: '🌳', color: '#10b981' });
        });
      }
      setPois(realPois);
    } catch (error) {
      console.warn("L'API de lieux est surchargée.");
      setPoiError(true);
    } finally {
      setLoadingPois(false);
    }
  };

  const openApplyModal = () => {
    if (!currentUser) {
      alert("Veuillez vous connecter pour postuler.");
      navigate('/login?type=habitat');
      return;
    }
    if (currentUser.id === listing.landlord_id) {
      navigate('/habitat/proprio/dashboard');
      return;
    }
    setShowApplyModal(true);
  };

  const submitApplication = async (e: React.FormEvent) => {
    e.preventDefault();
    setApplying(true);
    const { data: profile } = await supabase.from('habitat_profiles').select('full_name').eq('id', currentUser.id).maybeSingle();
    const tenantFullName = profile?.full_name || 'Locataire Forfeo';

    const { error } = await supabase.from('habitat_applications').insert([{
      tenant_id: currentUser.id, landlord_id: listing.landlord_id, listing_id: listing.id, tenant_name: tenantFullName,
      tenant_email: currentUser.email, tenant_phone: applyForm.phone, tenant_income: applyForm.income, message: applyForm.message
    }]);

    if (!error) {
      setHasApplied(true); setShowApplyModal(false);
      alert("Candidature envoyée avec succès !");
    } else {
      alert("Erreur lors de l'envoi : " + error.message);
    }
    setApplying(false);
  };

  const deleteListing = async () => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer définitivement cette annonce ?")) {
      const { error } = await supabase.from('habitat_listings').delete().eq('id', listing.id);
      if (!error) {
        alert("Annonce supprimée avec succès.");
        navigate('/habitat/proprio/dashboard');
      } else {
        alert("Erreur lors de la suppression : " + error.message);
      }
    }
  };

  const getEmbedUrl = (url: string) => {
    if (!url) return '';
    if (url.includes('youtube.com/watch?v=')) return url.replace('watch?v=', 'embed/');
    if (url.includes('youtu.be/')) return url.replace('youtu.be/', 'youtube.com/embed/');
    return url;
  };

  const displayedPOIs = useMemo(() => {
    if (!pois || Object.keys(pois).length === 0) return [];
    if (searchPoi.trim() !== '') {
      const allPois = Object.values(pois).flat() as any[];
      return allPois.filter(poi => poi.name.toLowerCase().includes(searchPoi.toLowerCase()));
    }
    return pois[activeCategory] || [];
  }, [searchPoi, activeCategory, pois]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div></div>;
  if (!listing) return <div className="min-h-screen flex items-center justify-center text-xl font-bold">Annonce introuvable.</div>;

  const isOwner = currentUser ? currentUser.id === listing.landlord_id : false;

  return (
    <div className="min-h-screen bg-slate-50 font-sans pt-20 pb-12 selection:bg-teal-500 selection:text-white">
      <Helmet>
        <title>{listing.title} | Forfeo Habitat</title>
        <meta name="description" content={listing.description?.substring(0, 150)} />
        <meta property="og:title" content={`${listing.title} - ${listing.monthly_rent}$/mois`} />
        <meta property="og:image" content={listing.images?.[0]} />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <Header />
      
      {/* ❌ L'ancienne barre flottante (qui se cachait sous le footer) a été supprimée */}

      <main className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center gap-4 mb-6">
          <button onClick={() => navigate('/habitat')} className="flex items-center gap-2 text-slate-500 hover:text-teal-600 font-bold transition">
            <ArrowLeft size={16} /> Retour aux annonces
          </button>
          <div className="w-px h-4 bg-slate-300"></div>
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-slate-500 hover:text-teal-600 font-bold transition">
            <Home size={16} /> Accueil
          </button>
        </div>

        <div className="bg-white rounded-[2rem] border border-slate-200 shadow-sm overflow-hidden relative">
          
          {listing.virtual_tour_url && (
            <button 
              onClick={() => setShow3DModal(true)} 
              className="absolute top-6 left-6 z-10 bg-blue-600/90 hover:bg-blue-600 backdrop-blur-md text-white px-6 py-3 rounded-full font-black flex items-center gap-2 shadow-2xl hover:scale-105 transition-all animate-pulse"
            >
              <View size={20}/> VISITE VIRTUELLE 3D
            </button>
          )}

          <div className="h-[300px] md:h-[450px] w-full bg-slate-100 flex gap-1 overflow-x-auto snap-x">
            {listing.images?.map((img: string, idx: number) => (
              <img key={idx} src={getOptimizedImageUrl(img)} alt={`Vue ${idx+1}`} className="h-full min-w-full md:min-w-[80%] object-cover snap-center" />
            ))}
          </div>

          <div className="p-6 md:p-12 flex flex-col lg:flex-row gap-12 relative">
            <div className="flex-1 space-y-8">
              
              {/* EN-TÊTE DE L'ANNONCE */}
              <div>
                <div className="flex items-center gap-2 text-sm font-bold text-teal-600 uppercase tracking-widest mb-3">
                  <MapPin size={16} /> {listing.address}, {listing.city}
                </div>
                <h1 className="text-3xl md:text-4xl font-black text-slate-900 mb-2">{listing.title}</h1>
                
                {/* ✅ PRIX VISIBLE SUR MOBILE SOUS LE TITRE */}
                <div className="text-3xl font-black text-slate-900 mb-6 md:hidden">
                  {listing.monthly_rent} $ <span className="text-sm text-slate-500 font-bold">/ mois</span>
                </div>

                <div className="flex items-center gap-6 text-sm font-bold text-slate-500 bg-slate-50 p-4 rounded-xl border border-slate-100 w-fit">
                  <span className="flex items-center gap-2"><BedDouble size={18} className="text-slate-400"/> {listing.bedrooms} Chambres</span>
                  <span className="flex items-center gap-2"><Bath size={18} className="text-slate-400"/> {listing.bathrooms} Salles de bain</span>
                </div>
              </div>

              {/* ✅ NOUVEAU BLOC : PROPRIÉTAIRE + BOUTON POSTULER INTÉGRÉ */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 bg-slate-50 p-6 rounded-3xl border border-slate-100 shadow-sm">
                
                <div onClick={() => navigate(`/habitat/proprio/${listing.landlord_id}`)} className="flex items-center gap-4 cursor-pointer group w-full sm:w-auto">
                  <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow-sm text-teal-600 group-hover:bg-teal-500 group-hover:text-white transition-colors shrink-0">
                    <Building size={24} />
                  </div>
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Proposé par</p>
                    <p className="text-sm font-bold text-slate-900 group-hover:text-teal-600 transition-colors flex items-center gap-1">
                      {landlordName} <CheckCircle size={14} className="text-teal-500" />
                    </p>
                  </div>
                </div>

                {/* Bouton d'action à côté du profil */}
                <div className="w-full sm:w-auto shrink-0 border-t border-slate-200 pt-4 sm:border-t-0 sm:pt-0 sm:pl-6 sm:border-l">
                  {isOwner ? (
                    <div className="flex gap-2">
                      <button onClick={deleteListing} className="p-3.5 bg-white text-red-500 rounded-xl hover:bg-red-50 border border-slate-200 transition-colors" title="Supprimer l'annonce">
                        <Trash2 size={18}/>
                      </button>
                      <button onClick={() => navigate('/habitat/proprio/dashboard')} className="flex-1 sm:flex-none py-3.5 px-6 rounded-xl font-black text-xs uppercase tracking-widest transition shadow-sm bg-teal-500 text-white hover:bg-teal-600 flex items-center justify-center">
                        Gérer
                      </button>
                    </div>
                  ) : (
                    <button 
                      onClick={openApplyModal} 
                      disabled={applying || hasApplied} 
                      className={`w-full sm:w-auto py-4 px-8 rounded-2xl font-black text-xs uppercase tracking-widest transition shadow-md flex items-center justify-center gap-2 ${hasApplied ? 'bg-slate-200 text-slate-500 cursor-not-allowed shadow-none' : 'bg-slate-900 text-white hover:bg-teal-500 hover:scale-105'}`}
                    >
                      {hasApplied ? <><CheckCircle size={18}/> Envoyé</> : 'Postuler'}
                    </button>
                  )}
                </div>

              </div>

              <div>
                <h3 className="text-xl font-black text-slate-900 mb-4">Description</h3>
                <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">{listing.description}</p>
              </div>

              {/* CARTE INTERACTIVE DU QUARTIER */}
              {coordinates && (
                <div className="pt-8 border-t border-slate-100 mt-8">
                  <h3 className="text-2xl font-black text-slate-900 mb-2 flex items-center gap-2">
                    <Map className="text-teal-500" /> Découvrez le quartier
                  </h3>
                  
                  {poiError && (
                    <div className="flex items-center gap-2 p-3 bg-amber-50 text-amber-700 text-xs font-bold rounded-xl mb-4 border border-amber-100">
                      <AlertTriangle size={16}/> L'affichage détaillé des lieux est temporairement limité.
                    </div>
                  )}

                  <div className="relative mb-4 mt-4">
                    <input 
                      type="text" 
                      placeholder="Chercher une école, un gym, un garage..." 
                      className="w-full pl-10 pr-4 py-3 rounded-xl border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-teal-500 transition-all font-medium"
                      value={searchPoi}
                      onChange={(e) => setSearchPoi(e.target.value)}
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  </div>

                  {searchPoi === '' && (
                    <div className="flex overflow-x-auto pb-2 mb-2 gap-2 snap-x hide-scrollbar">
                      <button onClick={() => setActiveCategory('groceries')} className={`shrink-0 px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition ${activeCategory === 'groceries' ? 'bg-amber-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                        <ShoppingCart size={16}/> Épiceries ({pois.groceries?.length || 0})
                      </button>
                      <button onClick={() => setActiveCategory('education')} className={`shrink-0 px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition ${activeCategory === 'education' ? 'bg-blue-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                        <GraduationCap size={16}/> Écoles ({pois.education?.length || 0})
                      </button>
                      <button onClick={() => setActiveCategory('pharmacies')} className={`shrink-0 px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition ${activeCategory === 'pharmacies' ? 'bg-red-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                        <Pill size={16}/> Pharmacies ({pois.pharmacies?.length || 0})
                      </button>
                      <button onClick={() => setActiveCategory('gyms')} className={`shrink-0 px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition ${activeCategory === 'gyms' ? 'bg-orange-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                        <Dumbbell size={16}/> Gyms ({pois.gyms?.length || 0})
                      </button>
                      <button onClick={() => setActiveCategory('garages')} className={`shrink-0 px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition ${activeCategory === 'garages' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                        <Wrench size={16}/> Garages ({pois.garages?.length || 0})
                      </button>
                      <button onClick={() => setActiveCategory('transit')} className={`shrink-0 px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition ${activeCategory === 'transit' ? 'bg-purple-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                        <Bus size={16}/> Transports ({pois.transit?.length || 0})
                      </button>
                      <button onClick={() => setActiveCategory('parks')} className={`shrink-0 px-4 py-2 rounded-xl font-bold flex items-center gap-2 transition ${activeCategory === 'parks' ? 'bg-green-500 text-white' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                        <TreePine size={16}/> Parcs ({pois.parks?.length || 0})
                      </button>
                    </div>
                  )}

                  <div className="h-[400px] w-full rounded-3xl overflow-hidden border border-slate-200 shadow-inner relative z-0 mt-2">
                    <MapContainer center={coordinates} zoom={14} scrollWheelZoom={false} className="h-full w-full z-0">
                      <TileLayer url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png" attribution='© CARTO' />
                      <Marker position={coordinates}><Popup>📍 Le Logement</Popup></Marker>
                      <Circle center={coordinates} radius={1500} pathOptions={{ color: '#14b8a6', fillColor: '#14b8a6', fillOpacity: 0.05 }} />
                      {!poiError && displayedPOIs.map((poi: any, index: number) => (
                        <Marker key={index} position={[poi.lat, poi.lon]} icon={createCustomIcon(poi.color, poi.icon)}>
                          <Popup><strong>{poi.name}</strong><br/>À {calculateDistanceKm(coordinates[0], coordinates[1], poi.lat, poi.lon)} km</Popup>
                        </Marker>
                      ))}
                    </MapContainer>
                  </div>
                </div>
              )}

              {/* SECTION MARKETING 3D */}
              {listing.virtual_tour_url && (
                <div className="bg-slate-900 text-white p-8 md:p-12 rounded-[2rem] mt-12 shadow-xl">
                  <div className="flex flex-col md:flex-row items-center gap-8">
                    <div className="flex-1 space-y-6">
                      <div className="inline-flex items-center gap-2 bg-blue-600/20 text-blue-400 px-4 py-2 rounded-full font-black text-xs uppercase tracking-widest">
                        <View size={16} /> Technologie Forfeo
                      </div>
                      <h3 className="text-3xl md:text-4xl font-black leading-tight">On vous simplifie les visites.</h3>
                      <p className="text-slate-400 leading-relaxed text-lg">Gagnez un temps précieux. Grâce à notre intégration de visite virtuelle 3D, promenez-vous dans votre futur chez-vous depuis votre canapé.</p>
                      <ul className="space-y-4 mt-6">
                        <li className="flex items-start gap-3">
                          <CheckCircle className="text-blue-500 shrink-0 mt-1" size={20} />
                          <p className="text-slate-300"><strong>Visitez 24h/24 :</strong> Explorez la propriété à votre rythme.</p>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="text-blue-500 shrink-0 mt-1" size={20} />
                          <p className="text-slate-300"><strong>Immersion totale :</strong> Circulez librement pour ressentir l'espace réel.</p>
                        </li>
                      </ul>
                    </div>
                    <div className="w-full md:w-1/3 aspect-square bg-slate-800 rounded-2xl border border-slate-700 flex flex-col items-center justify-center p-6 text-center">
                      <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mb-4 animate-pulse shadow-lg shadow-blue-600/50">
                        <View size={32} className="text-white" />
                      </div>
                      <h4 className="font-black text-xl mb-2">Essayez-le maintenant</h4>
                      <p className="text-sm text-slate-400">Cliquez sur le bouton bleu flottant sur les photos.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ✅ SIDEBAR DESKTOP : Gardée pour le rappel du prix */}
            <div className="hidden lg:block w-[350px] shrink-0">
              <div className="bg-white rounded-3xl border border-slate-200 shadow-xl p-6 sticky top-28">
                <div className="text-4xl font-black text-slate-900 mb-1">{listing.monthly_rent} $ <span className="text-lg text-slate-500 font-bold">/ mois</span></div>
                <div className="flex items-center gap-2 text-sm font-medium text-slate-500 mb-8 pb-6 border-b border-slate-100 font-sans"><Calendar size={16} /> Disponible le {listing.available_from ? new Date(listing.available_from).toLocaleDateString('fr-CA') : 'Maintenant'}</div>
                
                {isOwner ? (
                  <button onClick={() => navigate('/habitat/proprio/dashboard')} className="w-full py-4 rounded-2xl font-black uppercase tracking-widest transition shadow-lg bg-teal-500 text-white hover:bg-teal-600 flex items-center justify-center gap-2 mb-4">
                    Gérer mon annonce
                  </button>
                ) : (
                  <button 
                    onClick={openApplyModal} 
                    disabled={applying || hasApplied} 
                    className={`w-full py-5 rounded-2xl font-black uppercase tracking-widest transition shadow-lg mb-4 flex items-center justify-center gap-2 ${hasApplied ? 'bg-slate-200 text-slate-500 cursor-not-allowed shadow-none' : 'bg-teal-500 text-white hover:bg-teal-600 shadow-teal-500/30 hover:scale-[1.02]'}`}
                  >
                    {hasApplied ? <><CheckCircle size={20}/> Dossier envoyé</> : 'Postuler maintenant'}
                  </button>
                )}

                <div className="bg-green-50 border border-green-100 p-4 rounded-xl">
                  <p className="text-[11px] text-green-800 font-bold flex items-start gap-2"><Shield className="w-4 h-4 shrink-0 text-green-600" /> Dossier sécurisé conforme Loi 25 (Québec).</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* MODALS */}
      {show3DModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/90 backdrop-blur-md">
          <div className="w-full h-full md:w-[90vw] md:h-[85vh] bg-slate-900 md:rounded-3xl overflow-hidden relative flex flex-col shadow-2xl">
            <div className="flex justify-between items-center p-4 text-white border-b border-slate-800">
              <h3 className="font-bold flex items-center gap-2"><View size={18}/> Visite Virtuelle Interactive</h3>
              <button onClick={() => setShow3DModal(false)} className="p-2 bg-slate-800 rounded-full hover:bg-slate-700 transition"><X size={20}/></button>
            </div>
            <iframe src={getEmbedUrl(listing.virtual_tour_url)} className="w-full flex-1 border-0" allowFullScreen allow="xr-spatial-tracking; gyroscope; accelerometer"></iframe>
          </div>
        </div>
      )}

      {showApplyModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in">
          <div className="bg-white rounded-[2.5rem] w-full max-w-lg shadow-2xl overflow-hidden border border-slate-200 pb-20 md:pb-0">
            <div className="p-8 border-b border-slate-100 flex justify-between items-center bg-teal-50">
              <div><h3 className="text-2xl font-black text-slate-900">Dernière étape</h3><p className="text-sm font-bold text-teal-600 mt-1">Candidature pour : {listing.title}</p></div>
              <button onClick={() => setShowApplyModal(false)} className="p-2 bg-white rounded-full text-slate-400 hover:text-slate-900 border border-slate-200 shadow-sm"><X size={20}/></button>
            </div>
            <form onSubmit={submitApplication} className="p-8 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2"><Phone size={14} className="text-teal-500"/> Téléphone</label><input required type="tel" value={applyForm.phone} onChange={e => setApplyForm({...applyForm, phone: e.target.value})} placeholder="514-000-0000" className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-teal-500 outline-none transition-all" /></div>
                <div><label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2"><DollarSign size={14} className="text-teal-500"/> Revenus / an</label><input required type="text" value={applyForm.income} onChange={e => setApplyForm({...applyForm, income: e.target.value})} placeholder="60 000 $" className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-teal-500 outline-none transition-all" /></div>
              </div>
              <div><label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 flex items-center gap-2"><MessageSquare size={14} className="text-teal-500"/> Message</label><textarea required value={applyForm.message} onChange={e => setApplyForm({...applyForm, message: e.target.value})} className="w-full p-4 bg-slate-50 rounded-xl border border-slate-200 font-bold text-slate-900 focus:bg-white focus:ring-2 focus:ring-teal-500 outline-none transition-all h-32 resize-none" /></div>
              <button type="submit" disabled={applying} className="w-full bg-slate-900 text-white py-5 rounded-2xl font-black uppercase tracking-widest hover:bg-teal-600 transition-colors shadow-lg flex items-center justify-center gap-2">{applying ? 'Envoi...' : 'Soumettre mon dossier'}</button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
